import { computed, ref } from "vue";
import ProcessManager from "@/components/Workflows/processManager";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import { useStore } from "@/core/services/store";
import Reporting from "@/components/Workflows/Reporting/reporting";
import { Error } from "@/core/plugins/swal";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import { useAppSave } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppSave";
import { useUtility } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useUtility";

const store = useStore();
const { xentralProjectHeader } = useUtility();

const BUILD_FL_WORKFLOW_ID = computed(() => store.getters.buildFlWorkflowId);
const GET_ARTICLE_COUNT_WORKFLOW_ID = computed(
  () => store.getters.getArticleCountWorkflowId
);

const lastStoppedIteration = ref({});
const showArticleImportModal = ref(false);
const xentralArticleStock = ref(0);
const xentralArticleErrorMessage = ref();
const isMounted = ref(false);
const timeout = ref();
const showArticleImportPrepareModal = ref(false);
const specificArticleImportModel = ref();
const specificArticleNumbers = computed(
  () =>
    specificArticleImportModel.value?.productNumbers
      ?.split(",")
      .map(number => number.trim()) ?? []
);
const currentArticleImportPrepareModalStep = ref("prepare"); // prepare, import

const { selectedIntegration } = useIntegration();

export const useArticleImport = () => {
  const { setProductSelectionValue, storeArea, save } = useAppSave();
  const channelSpecificName = computed(
    () =>
      selectedIntegration?.value?.name
        ?.replace(store.getters.selectedApp.name, "")
        .trim() ?? ""
  );
  const hasErrorMessage = computed(() => !!xentralArticleErrorMessage.value);

  const currentArticleImportStatus = computed(() => {
    return lastStoppedIteration.value?.case_identifier ?? "0 / 0";
  });

  const isFinished = computed(() => {
    const parts = currentArticleImportStatus.value.toString().split("/");
    if (parts.length !== 2) {
      return true;
    }
    const numerator = parseFloat(parts[0].trim());
    const denominator = parseFloat(parts[1].trim());

    return numerator === denominator;
  });

  async function saveAction() {
    await save(true);
    getXentralArticleStock();
  }

  async function runCFLWorkflow() {
    try {
      currentArticleImportPrepareModalStep.value = "prepare";
      showArticleImportPrepareModal.value = false;
      showArticleImportModal.value = false;

      const integration = selectedIntegration?.value;

      // Store productsSelected-preset only the first time to prevent duplicate job inside connect
      if (!integration?.productsSelected?.id) {
        const response = await storeArea({
          name: "productsSelected",
          label: "productsSelected",
          type: "json",
          project_id: integration.id,
          value: {
            productsSelected: true
          }
        });

        integration.productsSelected = response?.data;

        await setProductSelectionValue(integration.id);
        await checkForPendingWorkingJobs();
        return;
      }

      const additionalParams = {
        channelSpecificName: channelSpecificName.value,
        project: store.getters.selectedProject
      };

      const articleNumbers = specificArticleNumbers.value?.filter(Boolean);

      if (articleNumbers?.length) {
        additionalParams.productNumbers = articleNumbers;
      }

      // Trigger flow "CONNECT / Build FL" in xentral preset
      addEventToLoadingQueue({ key: "buildFl" });
      const header = !store.getters.useXentralProjectForSync
        ? {}
        : xentralProjectHeader.value;
      const response = await ProcessManager.run(
        BUILD_FL_WORKFLOW_ID.value,
        {
          dispatchImmediately: true,
          params: additionalParams
        },
        header
      );

      lastStoppedIteration.value = response.data ?? null;
    } catch (error) {
      Error(error);
    } finally {
      specificArticleImportModel.value = undefined;
      removeEventFromLoadingQueue({ key: "buildFl" });
    }
  }

  async function checkForPendingWorkingJobs() {
    await getLastStoppedJob();

    if (isMounted) {
      timeout.value = setTimeout(checkForPendingWorkingJobs, 4000);
    }
  }

  async function getLastStoppedJob() {
    let params = {
      page: 1,
      perPage: 1,
      filter: [
        {
          key: "process_id",
          op: "equals",
          value: BUILD_FL_WORKFLOW_ID.value
        },
        {
          key: "status",
          op: "equals",
          value: "process.stopped"
        },
        {
          key: "content_identifier",
          op: "in",
          value: [
            channelSpecificName.value + " Finished",
            channelSpecificName.value + "\u00A0Finished",
            channelSpecificName.value
          ]
        }
      ]
    };

    lastStoppedIteration.value = await getIteration(params);
  }

  async function getIteration(params) {
    try {
      const header = !store.getters.useXentralProjectForSync
        ? {}
        : xentralProjectHeader.value;
      const response = await Reporting.getIterations(
        BUILD_FL_WORKFLOW_ID.value,
        params,
        header,
        true
      );
      return response.data?.[0] ?? {};
    } catch (error) {
      Error(error);
    }
    return {};
  }

  function getXentralArticleStock(
    params = {},
    toggleArticleImportModal = true
  ) {
    addEventToLoadingQueue({ key: "stock" });

    // Reset the error message
    xentralArticleErrorMessage.value = undefined;

    params.channelSpecificName = channelSpecificName.value;
    params.project = store.getters.selectedProject;

    // Trigger flow "CONNECT / Get article count" in xentral preset
    const header = !store.getters.useXentralProjectForSync
      ? {}
      : xentralProjectHeader.value;
    ProcessManager.run(
      GET_ARTICLE_COUNT_WORKFLOW_ID.value,
      {
        dispatchImmediately: true,
        params: params
      },
      header
    )
      .then(response => {
        let stock = response.data?.return?.data;

        if (response.data?.return?.success === false) {
          xentralArticleErrorMessage.value = response.data?.return?.data;
        } else {
          if (isNaN(stock)) return;

          xentralArticleStock.value = stock;
        }

        if (toggleArticleImportModal) {
          showArticleImportModal.value = true;
        }
      })
      .catch(error => Error(error))
      .finally(() => removeEventFromLoadingQueue({ key: "stock" }));
  }

  async function importSpecificArticle() {
    await save(true);

    const params = {
      productNumbers: specificArticleNumbers.value
    };

    getXentralArticleStock(params, false);

    currentArticleImportPrepareModalStep.value = "import";
  }

  return {
    checkForPendingWorkingJobs,
    runCFLWorkflow,
    importSpecificArticle,
    saveAction,
    currentArticleImportStatus,
    isMounted,
    lastStoppedIteration,
    showArticleImportModal,
    showArticleImportPrepareModal,
    specificArticleImportModel,
    specificArticleNumbers,
    xentralArticleStock,
    timeout,
    currentArticleImportPrepareModalStep,
    isFinished,
    xentralArticleErrorMessage,
    hasErrorMessage
  };
};
